import { ref, computed, watch, onMounted, onUnmounted } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { useDisplay } from 'vuetify'
import { format } from 'date-fns';
import { getIconClass as iconClass } from '@rdrive/helper-icon-class';
import { useAnalytics } from '@/utils/useAnalytics';
import { useTeamAssignment } from '@/composables/useTeamAssignment';

export default (props) => {
  const store = useStore();
  const { t } = useI18n();
  const settings = computed(() => store.state.settings);
  const { mobile, mdAndUp } = useDisplay()
  const isMobile = computed(() => mobile.value && !mdAndUp.value)

  const openPanels = ref([]);
  const isMounted = ref(false);

  const conn = store.getters.getDBConnection;

  // C4: the dialog is dumb; the page owns the queries and the assignee PUT.
  // `allItems` (the store's full item list) drives the dialog's per-user badge
  // counts, while the PUT loop runs against `loadedItems` — the two lists differ
  // and both are needed (R13).
  const { trackEvent } = useAnalytics();
  const allItems = computed(() => store.getters.getItems);
  const { teamUsers, loadTeamUsers, assigning, assign } = useTeamAssignment({
    conn,
    apiClient: computed(() => store.state.apiClient),
    apiUrl: computed(() => store.getters.getApiUrl),
    projectCode: computed(() => store.getters.getProjectCode),
    projectName: computed(() => store.getters.getProjectName),
    trackEvent
  });

  // Load lookup objects from DuckDB directly since Vuex store is not populated
  const categoriesObj = ref({});
  const itemTemplatesObj = ref({});
  const formTemplatesObj = ref({});
  const locationsObj = ref({});
  const statusTemplatesObj = ref({});

  const loadLookupDataFromDB = async () => {
    if (!isMounted.value) return;
    try {
      // Load categories
      const categoriesResult = await conn.query('SELECT * FROM categories');
      categoriesResult.toArray().forEach(row => {
        const cat = row.toJSON();
        const id = cat.Id || cat.id;
        if (id) {
          categoriesObj.value[id] = {
            id: id,
            title: cat.Title || cat.title || '',
            icon: cat.Icon || cat.icon || ''
          };
        }
      });

      // Load item templates
      const itemTemplatesResult = await conn.query('SELECT * FROM item_templates');
      itemTemplatesResult.toArray().forEach(row => {
        const tmpl = row.toJSON();
        const id = tmpl.Id || tmpl.id;
        if (id) {
          itemTemplatesObj.value[id] = {
            id: id,
            title: tmpl.Title || tmpl.title || '',
            categoryId: tmpl.CategoryId || tmpl.categoryId || '',
            subcategoryId: tmpl.SubcategoryId || tmpl.subcategoryId || ''
          };
        }
      });

      // Load form templates
      const formTemplatesResult = await conn.query('SELECT * FROM form_templates');
      formTemplatesResult.toArray().forEach(row => {
        const tmpl = row.toJSON();
        const id = tmpl.Id || tmpl.id;
        if (id) {
          formTemplatesObj.value[id] = {
            id: id,
            title: tmpl.Title || tmpl.title || ''
          };
        }
      });

      // Load locations with drawing info
      const locationsResult = await conn.query(`
      SELECT l.*, d.Title as drawingTitle
      FROM locations l
      LEFT JOIN drawings d ON l.DrawingId = d.Id
    `);
      locationsResult.toArray().forEach(row => {
        const loc = row.toJSON();
        const id = loc.Id || loc.id;
        if (id) {
          locationsObj.value[id] = {
            id: id,
            title: loc.Title || loc.title || '',
            drawingId: loc.DrawingId || loc.drawingId || '',
            drawingTitle: loc.drawingTitle || ''
          };
        }
      });

      // Load status templates
      const statusTemplatesResult = await conn.query('SELECT * FROM status_templates');
      statusTemplatesResult.toArray().forEach(row => {
        const status = row.toJSON();
        const id = status.Id || status.id;
        if (id) {
          statusTemplatesObj.value[id] = {
            id: id,
            title: status.Title || status.title || '',
            color: status.Color || status.color || '#grey'
          };
        }
      });

    } catch (error) {
      console.error('Error loading lookup data from DB:', error);
    }
  };

  // Load teams from DuckDB directly since Vuex store's teamsObj is not populated
  const teamsObj = ref({});

  const loadTeamsFromDB = async () => {
    if (!isMounted.value) return;
    if (Object.keys(teamsObj.value).length > 0) return;
    try {
      const result = await conn.query('SELECT * FROM teams');
      const teams = {};
      result.toArray().forEach(row => {
        const team = row.toJSON();
        const id = team.Id || team.id;
        if (id) {
          teams[id] = {
            id: id,
            name: team.Name || team.name || '',
            teamProfileTitle: team.TeamProfileTitle || team.teamProfileTitle || ''
          };
        }
      });
      teamsObj.value = teams;
    } catch (error) {
      console.error('Error loading teams from DB:', error);
    }
  };

  // Load users from DuckDB directly since Vuex store's usersObj is not populated
  const usersObj = ref({});

  const loadUsersFromDB = async () => {
    if (!isMounted.value) return;
    if (Object.keys(usersObj.value).length > 0) return;
    try {
      // Load users from key-value structured table
      // Schema: Key (team ID) | Value (JSON array of users)
      const result = await conn.query('SELECT Key, Value FROM users');
      const users = {};

      result.toArray().forEach(row => {
        const rowData = row.toJSON();
        let teamUsers = rowData.Value || rowData.value;

        // Parse the JSON string to get array of users
        if (typeof teamUsers === 'string') {
          try {
            teamUsers = JSON.parse(teamUsers);
          } catch {
            teamUsers = [];
          }
        }

        // Extract users from this team
        if (Array.isArray(teamUsers)) {
          teamUsers.forEach(u => {
            const id = u.Id || u.id;
            if (id) {
              // Only add if not already added (avoid duplicates across teams)
              if (!users[id]) {
                users[id] = {
                  displayName: u.DisplayName || u.displayName || '',
                  avatarUrl: u.AvatarUrl || u.avatarUrl || ''
                };
              }
            }
          });
        }
      });

      usersObj.value = users;
    } catch (error) {
      console.error('Error loading users from DB:', error);
    }
  };
  const selectedSubItems = ref([]);
  const expanded = ref([]);
  const selectedTeam = ref([t('unAssigned')]);

  const searchQuery = ref('');
  const listSearchQuery = ref('');

  const showAssignmentDialog = ref(false);

  const headers = computed(() => {
    const baseHeaders = [
      { title: t('id'), key: 'clientId', align: 'start', sortable: true },
      { title: t('item'), key: 'profile', align: 'start', sortable: true },
      { title: t('assignedTo'), key: 'assignees', sortable: false },
      { title: t('location'), key: 'location', align: 'start', sortable: true },
      { title: t('updatedDate'), key: 'updatedDate', align: 'start', sortable: true },
      { title: t('status'), key: 'status', align: 'start', sortable: true },
    ];

    return baseHeaders;
  });

  // Load items from DuckDB
  const loadedItems = ref([]);
  const dataLoaded = computed(() => store.getters.getDataLoaded);

  const loadItemsFromDB = async () => {
    if (!isMounted.value) return;
    if (!dataLoaded.value) return;

    try {
      // Build team filter for assignable teams
      const assignableTeamIdsList = Object.keys(props.assignableTeamIds)
        .filter(id => props.assignableTeamIds[id])
        .map(id => `'${id}'`)
        .join(',');

      if (!assignableTeamIdsList) {
        loadedItems.value = [];
        return;
      }

      // Include unassigned items (empty assigneeTeamId) OR items assigned to manageable teams
      const query = `
      SELECT
        fi.clientId,
        fi.id,
        fi.locationId,
        fi.subItemId,
        fi.statusId,
        fi.statusCreatedDate,
        fi.assigneeTeamId,
        fi.assigneeUserIds,
        fi.subItemTemplateId as itemTemplateId,
        fi.profileId,
        fi.type
      FROM filteredItems fi
      WHERE fi.assigneeTeamId IN (${assignableTeamIdsList})
         OR fi.assigneeTeamId IS NULL
         OR fi.assigneeTeamId = ''
      ORDER BY fi.statusCreatedDate DESC
    `;

      const result = await conn.query(query);
      const rows = result.toArray().map(row => row.toJSON());

      // Group by item id
      const itemsMap = {};
      rows.forEach(row => {
        // Parse assigneeUserIds
        let assigneeUserIds = [];
        if (row.assigneeUserIds) {
          if (typeof row.assigneeUserIds === 'string') {
            try {
              assigneeUserIds = JSON.parse(row.assigneeUserIds);
            } catch (e) {
              console.error('Failed to parse assigneeUserIds:', row.assigneeUserIds, e);
              assigneeUserIds = [];
            }
          } else if (Array.isArray(row.assigneeUserIds)) {
            assigneeUserIds = row.assigneeUserIds;
          } else {
            // Handle DuckDB objects or other data types
            try {
              assigneeUserIds = Array.from(row.assigneeUserIds);
            } catch {
              assigneeUserIds = [];
            }
          }
        }

        if (!itemsMap[row.id]) {
          itemsMap[row.id] = {
            id: row.id,
            clientId: row.clientId,
            locationId: row.locationId,
            profileId: row.profileId,
            type: row.type,
            subItems: []
          };
        }

        itemsMap[row.id].subItems.push({
          id: row.subItemId,
          statusId: row.statusId,
          statusCreatedDate: row.statusCreatedDate,
          assigneeTeamId: row.assigneeTeamId,
          assigneeUserIds: assigneeUserIds,
          itemTemplateId: row.itemTemplateId
        });
      });

      loadedItems.value = Object.values(itemsMap);
    } catch (error) {
      console.error('Error loading items from DB:', error);
      loadedItems.value = [];
    }
  };

  const filteredItems = computed(() => {
    if (!loadedItems.value.length) {
      return [];
    }

    const currentSelectedTeam = selectedTeam.value[0];

    let filtered = loadedItems.value.map(item => ({
      ...item,
      subItems: item.subItems.filter(subItem => {
        // Filter by selected team (compare as strings to handle type mismatches)
        if (currentSelectedTeam === t('unAssigned')) {
          if (subItem.assigneeTeamId) return false;
        } else {
          if (String(subItem.assigneeTeamId) !== String(currentSelectedTeam)) return false;
        }

        // Apply search query
        if (searchQuery.value) {
          const search = searchQuery.value.toLowerCase();
          const itemValues = Object.values(item).concat(Object.values(subItem));
          const matchesSearch = itemValues.some(value => String(value).toLowerCase().includes(search))
            || (itemTemplatesObj.value[item.profileId]?.title || '').toLowerCase().includes(search)
            || (locationsObj.value[item.locationId]?.drawingTitle || '').toLowerCase().includes(search)
            || (locationsObj.value[item.locationId]?.title || '').toLowerCase().includes(search)
            || (statusTemplatesObj.value[subItem.statusId]?.title || '').toLowerCase().includes(search)
            || subItem.assigneeUserIds.some(userId =>
                usersObj.value[userId] &&
                usersObj.value[userId].displayName.toLowerCase().includes(search)
              )
            || (subItem.assigneeTeamId && (teamsObj.value[subItem.assigneeTeamId]?.name.toLowerCase().includes(search) ?? false));

          if (!matchesSearch) {
            return false;
          }
        }
        return true;
      })
    }));

    // Remove items with empty subItems array
    filtered = filtered.filter(item => item.subItems.length > 0);

    // Map the filtered items to include the computed properties
    return filtered.map(item => ({
      ...item,
      itemTitle: itemTemplatesObj.value[item.profileId]?.title || '',
      itemDrawingLocation: locationsObj.value[item.locationId]?.drawingTitle || '',
      itemLocation: locationsObj.value[item.locationId]?.title || '',
      subItems: item.subItems.map(subItem => ({
        ...subItem,
        itemUpdated: subItem.statusCreatedDate,
        itemStatus: statusTemplatesObj.value[subItem.statusId]?.title || '',
        itemUser: selectedTeam.value[0] !== t('unAssigned') && subItem.assigneeTeamId ? teamsObj.value[subItem.assigneeTeamId]?.name || '' : ''
      }))
    }));
  });

  const groupedTeams = computed(() => {
    const grouped = {};
    if (teamsObj.value) {
      Object.values(teamsObj.value).forEach(team => {
        if (props.assignableTeamIds[team.id]) {
          if (!grouped[team.teamProfileTitle]) {
            grouped[team.teamProfileTitle] = [];
          }
          grouped[team.teamProfileTitle].push(team);
        }
      });
    }
    return grouped;
  });

  const filteredGroupedTeams = computed(() => {
    const filtered = {};
    for (const [profile, teams] of Object.entries(groupedTeams.value)) {
      const filteredTeams = teams.filter(team => {
        const teamItemCount = getOriginalTeamCount(team.id);
        const matchesSearch = !listSearchQuery.value || 
          team.name.toLowerCase().includes(listSearchQuery.value.toLowerCase());
        return teamItemCount > 0 && matchesSearch;
      });
    
      if (filteredTeams.length > 0 || 
          (profile.toLowerCase().includes(listSearchQuery.value.toLowerCase()))) {
        filtered[profile] = filteredTeams;
      }
    }
    return filtered;
  });

  const selectAll = () => {
    selectedSubItems.value = filteredItems.value.flatMap(item => 
      item.subItems.map(subItem => subItem.id)
    );
  };

  const selectUnassignedUsers = () => {
    if (selectedTeam.value[0] === t('unAssigned')) return;

    const unassignedSubItemIds = filteredItems.value.flatMap(item => 
      item.subItems
        .filter(subItem => subItem.assigneeUserIds.length === 0)
        .map(subItem => subItem.id)
    );
  
    selectedSubItems.value = [...new Set([...selectedSubItems.value, ...unassignedSubItemIds])];
  };

  const deselectAll = () => {
    selectedSubItems.value = [];
  };

  const getInitials = (name) => {
    if (!name) return '';
    const words = name.split(' ');
    if (words.length >= 2) {
      return (words[0][0] + words[1][0]).toUpperCase();
    } else {
      return words[0].substring(0, 2).toUpperCase();
    }
  };

  const getFormTemplateTitleIfDifferent = (subItemTemplateId, itemProfileId) => {
    const formTemplate = formTemplatesObj.value[subItemTemplateId];
    if (!formTemplate) {
      return ''; // Must be an issue template
    }

    const itemProfile = itemTemplatesObj.value[itemProfileId];
    if (!itemProfile) {
      return 'Item Profile does not exist';
    }

    return formTemplate.title !== itemProfile.title ? formTemplate.title : '';
  };

  const getFormTemplateTitle = (subItemTemplateId) => {
    const formTemplate = formTemplatesObj.value[subItemTemplateId];
    if (formTemplate) {
      return formTemplate.title;
    }
    return '';
  };


  const getRandomColor = (id) => {
    const colors = ['#F44336', '#E91E63', '#9C27B0', '#673AB7', '#3F51B5', '#2196F3', '#03A9F4', '#00BCD4', '#009688', '#4CAF50', '#8BC34A', '#CDDC39', '#FFC107', '#FF9800', '#FF5722'];
  
    if (!id) {
      return colors[Math.floor(Math.random() * colors.length)];
    }
  
    const index = id.toString().split('').reduce((acc, char) => acc + char.charCodeAt(0), 0) % colors.length;
    return colors[index];
  };

  const getOriginalTeamCount = (teamId) => {
    return teamCount.value[teamId]?.originalCount || 0;
  };

  const selectTeamInFilterContext = (teamId) => {
    selectedTeam.value = [teamId];
    // The filteredItems computed will automatically update based on selectedTeam
  };


  const getTotalProfileCount = (profileTeams) => {
    if (Array.isArray(profileTeams)) {
      return profileTeams.reduce((total, team) => total + getOriginalTeamCount(team.id), 0);
    } else if (typeof profileTeams === 'object' && profileTeams !== null) {
      return Object.values(profileTeams).reduce((total, team) => total + getOriginalTeamCount(team.id), 0);
    } else {
      console.warn('profileTeams is neither an array nor an object:', profileTeams);
      return 0;
    }
  };




  const teamCount = computed(() => {
    const newTeamCount = {};
    Object.keys(teamsObj.value).forEach(teamId => {
      newTeamCount[teamId] = { originalCount: 0, filteredCount: 0 };
    });
    newTeamCount[''] = { originalCount: 0, filteredCount: 0 }; // For unassigned items

    loadedItems.value.forEach(item => {
      item.subItems.forEach(subItem => {
        const teamId = subItem.assigneeTeamId || '';
        if (newTeamCount[teamId]) {
          newTeamCount[teamId].originalCount++;
        }
      });
    });

    return newTeamCount;
  });

  const openAssignmentDialog = async () => {
    showAssignmentDialog.value = true;
    await loadTeamUsers();
  };

  const handleAssign = async (assignmentData) => {
    // assignmentData contains: { teamId, userIds, subItemIds }
    const ok = await assign({
      subItemIds: assignmentData.subItemIds,
      items: loadedItems.value,
      teamId: assignmentData.teamId,
      userIds: assignmentData.userIds
    });

    // A failed PUT leaves the dialog open and the local items untouched, as before
    if (!ok) return;

    // Update the local loadedItems with the new assignment data
    if (assignmentData && assignmentData.subItemIds && assignmentData.subItemIds.length > 0) {
      loadedItems.value = loadedItems.value.map(item => ({
        ...item,
        subItems: item.subItems.map(subItem => {
          if (assignmentData.subItemIds.includes(subItem.id)) {
            return {
              ...subItem,
              assigneeTeamId: assignmentData.teamId,
              assigneeUserIds: assignmentData.userIds
            };
          }
          return subItem;
        })
      }));
    }
    selectedSubItems.value = [];
    showAssignmentDialog.value = false;
  };

  const hasAssignees = (item) => {
    return item.subItems.some(subItem => subItem.assigneeUserIds && subItem.assigneeUserIds.length > 0);
  };

  const getUniqueAssigneeUserIds = (item) => {
    const assigneeUserIds = item.subItems.flatMap(subItem => subItem.assigneeUserIds || []);
    return [...new Set(assigneeUserIds)].slice(0, 5);
  };


  const formatDate = (dateString) => {
    if (!dateString) return '';
    // Handle DuckDB timestamp which may be in milliseconds or as ISO string
    let date;
    if (typeof dateString === 'number') {
      // If it's a number, check if it's in milliseconds or seconds
      // DuckDB timestamps are typically in milliseconds
      date = new Date(dateString);
    } else {
      date = new Date(dateString);
    }
    // Validate the date is reasonable (not in the far future)
    if (isNaN(date.getTime())) return '';
    return format(date, 'yyyy-MM-dd');
  };

  const memoize = (fn) => {
    const cache = new Map();
    return (...args) => {
      const key = JSON.stringify(args);
      if (cache.has(key)) {
        return cache.get(key);
      }
      const result = fn(...args);
      cache.set(key, result);
      return result;
    };
  };

  // The shared mapping (@rdrive/helper-icon-class), wrapped as `{ icon }` for the template's v-bind.
  const getIconClass = memoize((icon) => ({ icon: iconClass(icon) }));

  // Safe accessor functions to prevent null reference errors
  const getItemTemplate = (profileId) => {
    return itemTemplatesObj.value[profileId] || { title: '', categoryId: '', subcategoryId: '' };
  };

  const getCategory = (categoryId) => {
    return categoriesObj.value[categoryId] || { icon: 'mdi-help-circle', title: '' };
  };

  const getLocation = (locationId) => {
    return locationsObj.value[locationId] || { title: '', drawingId: '', drawingTitle: '' };
  };

  const getStatusTemplate = (statusId) => {
    return statusTemplatesObj.value[statusId] || { title: '', color: '#grey' };
  };

  const getTeam = (teamId) => {
    return teamsObj.value[teamId] || { name: '', teamProfileTitle: '' };
  };

  const getUser = (userId) => {
    return usersObj.value[userId] || { displayName: 'Unknown User', avatarUrl: '' };
  };

  const getAvatarUrl = (avatarUrl) => {
    if (!avatarUrl) {
      return '';
    }
    // If the URL already includes a protocol, return as-is
    if (avatarUrl.startsWith('http://') || avatarUrl.startsWith('https://')) {
      return avatarUrl;
    } else {
      // Otherwise, prepend https://
      return 'https://' + avatarUrl;
    }
  };

  const toggleExpand = (item) => {
    const index = expanded.value.indexOf(item.id)
    if (index === -1) {
      expanded.value.push(item.id)
    } else {
      expanded.value.splice(index, 1)
    }
  }

  const isExpanded = (item) => {
    return expanded.value.includes(item.id)
  }

  const expandIcon = computed(() => (item) => {
    return isExpanded(item) ? 'mdi-chevron-down' : 'mdi-chevron-right'
  })

  // buildMode is hardcoded to 'webapp' and $launchUrl is not defined in this
  // repo, so the old 'production' branch could only ever throw (R6)
  const openUrl = (url) => window.open(url, '_blank');

  const setFirstTeamAsSelected = () => {
    const firstTeamId = Object.keys(props.assignableTeamIds)[0];
    if (firstTeamId) {
      selectedTeam.value = [firstTeamId];
      selectTeamInFilterContext(firstTeamId);
    } else {
      selectedTeam.value = [t('unAssigned')];
      selectTeamInFilterContext(t('unAssigned'));
    }
  };

  watch(() => props.assignableTeamIds, (newValue) => {
    if (isMounted.value && Object.keys(newValue).length > 0) {
      setFirstTeamAsSelected();
    }
  });

  onMounted(async () => {
    isMounted.value = true;
    await Promise.all([loadUsersFromDB(), loadTeamsFromDB(), loadLookupDataFromDB(), loadItemsFromDB(), loadTeamUsers()]);
    if (!isMounted.value) return; // Component was unmounted during async operations
    setFirstTeamAsSelected();
    openPanels.value = Object.keys(filteredGroupedTeams.value);
  });

  onUnmounted(() => {
    isMounted.value = false;
  });

  // `t` is a template binding: the Assign toolbar's tooltip calls `t('unAssigned')`
  // directly (the app-global useI18n() composer), not `$t`.
  //
  // layout-check-unbound: showSubItemsDialog
  // The mobile row's sub-items button runs `showSubItemsDialog = true; …`, but this
  // page never declared a `showSubItemsDialog` — it compiled to `_ctx.showSubItemsDialog`
  // under <script setup> as well, so the write lands on the render context and nothing
  // reads it. A pure-refactor split must not invent the binding (COMPONENT_LAYOUT.md).
  return {
    t,
    settings,
    isMobile,
    openPanels,
    allItems,
    teamUsers,
    assigning,
    selectedSubItems,
    expanded,
    selectedTeam,
    searchQuery,
    listSearchQuery,
    showAssignmentDialog,
    headers,
    filteredItems,
    filteredGroupedTeams,
    getOriginalTeamCount,
    selectAll,
    selectUnassignedUsers,
    deselectAll,
    getInitials,
    getFormTemplateTitleIfDifferent,
    getFormTemplateTitle,
    getRandomColor,
    selectTeamInFilterContext,
    getTotalProfileCount,
    openAssignmentDialog,
    handleAssign,
    hasAssignees,
    getUniqueAssigneeUserIds,
    formatDate,
    getIconClass,
    getItemTemplate,
    getCategory,
    getLocation,
    getStatusTemplate,
    getTeam,
    getUser,
    getAvatarUrl,
    toggleExpand,
    isExpanded,
    expandIcon,
    openUrl
  };
};
